import React from 'react';
import { PanelRightOpen, Sparkles } from 'lucide-react';

import { Button } from '@components/ui/button';
import { cn } from '@components/lib/utils';

import { getStatusConfig } from './utils';
import type { SessionStatus } from './types';

const GRADIENT = 'bg-[linear-gradient(143.42deg,#DFCCFF_7.32%,#5184FB_96.34%)]';

interface CollapsedViewProps {
  forceCollapse: boolean;
  setIsCollapse: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  sessionStatus?: SessionStatus;
}

const CollapsedView: React.FC<CollapsedViewProps> = React.memo(
  ({ forceCollapse, setIsCollapse, isLoading, sessionStatus }) => {
    // Surface the most demanding state on the collapsed badge so the user
    // notices background work / a pending question without expanding.
    const badgeStatus: SessionStatus | undefined =
      sessionStatus === 'WAITING_FOR_USER' || sessionStatus === 'ERROR'
        ? sessionStatus
        : isLoading || sessionStatus === 'PROCESSING'
        ? 'PROCESSING'
        : undefined;

    // The whole rail is click-to-expand (wrapper onClick); stop propagation
    // here so these controls don't double-fire the toggle.
    const expand = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!forceCollapse) setIsCollapse(false);
    };

    return (
      <div className="flex h-full flex-col items-center gap-4">
        {!forceCollapse && (
          <Button
            variant="ghost"
            size="icon"
            onClick={expand}
            aria-label="Open AI Assistant"
            className="rounded-full"
          >
            <PanelRightOpen className="text-marble-bg-600" />
          </Button>
        )}

        <button
          type="button"
          onClick={expand}
          aria-label="Open AI Assistant"
          className={cn(
            'relative flex size-9 items-center justify-center rounded-full text-white',
            GRADIENT,
            !forceCollapse && 'cursor-pointer',
          )}
        >
          <Sparkles className="size-4" />
          {badgeStatus && (
            <span className="absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-card">
              <span
                className={cn(
                  'size-2 rounded-full',
                  badgeStatus !== 'ERROR' && 'animate-pulse',
                )}
                style={{ background: getStatusConfig(badgeStatus).color }}
              />
            </span>
          )}
        </button>

        <div className="mt-2 text-xs font-semibold tracking-widest text-marble-bg-500 [writing-mode:vertical-rl]">
          AI ASSISTANT
        </div>
      </div>
    );
  },
);

CollapsedView.displayName = 'CollapsedView';

export default CollapsedView;
